import { FormFieldSelect } from '@/components/FormFieldSelect';
import { useFormContext } from 'react-hook-form';
import {
  ADDRESS_LINE_VALIDATION,
  caseTypesWithWitnessFees,
  SERVE_TYPE,
  STATE_VALIDATION,
  ZIP_CODE_VALIDATION,
} from '@/utils/constant';
import FormFieldComponent from '@/components/FormFieldComponent';
import { ChevronDown, ChevronUp, Search, User, Building, Plus } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldTextArea from '@/components/FormFieldTextArea';
import { Copy } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import FormFieldSearch from '@/components/FormFieldSearch';
import FormFieldMaskedInput from '@/components/FormFieldMaskedInput';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import { genUniqueId } from '@/lib/utils';
import { getUsersByTeam, serverSearch, teamSearch } from '@/services/Network';
import {
  affidavitTypeGetAll,
  caseDocumentTypeGetAll,
  useCaseDocumentTypeCreate,
  WorkOrderResponseSchemaDocumentsItem,
} from '@brainvaulttech/dgr-react-sdk';
import { useQuery } from '@tanstack/react-query';
import Loader from '@/components/Loader';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import FormFieldPhone from '@/components/FormFieldPhone';
import { WorkOrderVendorForm } from './WorkOrderVendorForm';
import { shouldAutoIncludeHomeInspection, WorkOrderServeTo } from '../pe.schema';
import { WorkOrderPayments } from './WorkOrderPayments';
import type { UserResponse } from '@/types/workOrderAttempt.types';
import { useUserStore } from '@/stores/user';
import type { TeamsResponse } from '@/types/team.types';
import { WorkOrderPaymentForm } from './WorkOrderPaymentForm';
import { PaymentType, WorkOrderPaymentStatus } from '@/types/workOrder.types';
import { hasFeature } from '@/services/featureService';
import { isAdminOrSupervisor } from '@/services/userService';
import { canCreateLinkedHomeInspection } from '../canCreateLinkedHomeInspection';

const SearchIconsProps = {
  Icon: Search,
  iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
};

export interface WorkOrderSchema {
  workOrderId?: string;
  address1?: string;
  address2?: string | null;
  city?: string;
  state?: string;
  zipCode?: string;
  toServe?: string;
  type?: string;
  affidavitTypeId?: string;
  serverId?: string;
  serverInstructions?: string | null;
  phone?: string;
  teamId?: string;
  userId?: string;
  user?: UserResponse | null;
  team?: TeamsResponse;
  addressType?: string;
  documents?: WorkOrderResponseSchemaDocumentsItem[];
  serveTo?: string;
  includeHomeInspection?: boolean;
  includeHomeInspectionManualOverride?: boolean;
}

const PeWoDetails = ({
  services,
  setServices,
  initialActiveWorkOrderId,
  showIncludeHomeInspection = false,
  includeHomeInspectionDisabled = false,
}: {
  services: { id: string; name: string; type: undefined | string; data: WorkOrderSchema | undefined }[];
  setServices: (
    services: { id: string; name: string; type: undefined | string; data: WorkOrderSchema | undefined }[]
  ) => void;
  initialActiveWorkOrderId?: string;
  /** Create/edit: Include Home Inspection checkbox (issues #420 / #421). */
  showIncludeHomeInspection?: boolean;
  /** Edit-lock / session block — disables Include Home Inspection toggle (REQ-3). */
  includeHomeInspectionDisabled?: boolean;
}) => {
  const {
    control,
    getValues,
    reset,
    watch,
    setValue,
    unregister,
    formState: { touchedFields },
    getFieldState,
    trigger,
  } = useFormContext();

  const hasChecksFeature = hasFeature('services.check');
  const userStore = useUserStore();
  const canShowIncludeHomeInspection = showIncludeHomeInspection && canCreateLinkedHomeInspection();

  const caseTypeId = watch('case.typeId') as string | undefined;
  const workOrdersWatch = watch('workOrders') as
    | { includeHomeInspectionManualOverride?: boolean }[]
    | undefined;

  useEffect(() => {
    if (!canShowIncludeHomeInspection) return;
    const workOrders = (getValues('workOrders') as { includeHomeInspectionManualOverride?: boolean }[]) || [];
    const autoChecked = shouldAutoIncludeHomeInspection(caseTypeId);
    workOrders.forEach((wo, index) => {
      if (wo?.includeHomeInspectionManualOverride) return;
      setValue(`workOrders[${index}].includeHomeInspection`, autoChecked, { shouldDirty: false });
    });
  }, [canShowIncludeHomeInspection, caseTypeId, workOrdersWatch?.length, getValues, setValue]);

  const [open, setOpen] = useState(false);
  const [openPrivateInfo, setOpenPrivateInfo] = useState<boolean>(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [newDocument, setNewDocument] = useState('');
  const [addDocumentField, setAddDocumentField] = useState(false);
  const [activeServiceId, setActiveServiceId] = useState<string>(services[0]?.id ?? genUniqueId());
  const [docRefreshkey, setDocRefreshKey] = useState(0);
  const [isSupervisorOrAdmin, setIsSupervisorOrAdmin] = useState(false);
  const {
    mutate: createCaseTypeDoc,
    status: caseDocTypeCreateStatus,
    error: caseDocTypeCreateError,
  } = useCaseDocumentTypeCreate();

  const docCreateHandler = (caseTypeId: string, documentName: string) => {
    const data = {
      caseTypeId,
      documentName,
    };
    createCaseTypeDoc(
      {
        data: data,
      },
      {
        onSuccess: () => {
          setDocRefreshKey((prev) => prev + 1);
          setAddDocumentField(false);
          setNewDocument('');
          unregister('otherDocName');
        },
      }
    );
  };

  const handlePrivateInfo = () => {
    setOpenPrivateInfo(!openPrivateInfo);
  };

  const handleServiceSwitch = (id: string) => {
    setActiveServiceId(id);
  };

  const duplicateService = (id: string) => {
    const index = services.findIndex((service) => service.id === id);
    if (index === -1) return;
    const serviceToDuplicate = (getValues(`workOrders`) as WorkOrderSchema[])[index];
    const dupServiceId = genUniqueId();
    if (serviceToDuplicate) {
      const duplicatedService = {
        id: dupServiceId,
        name: serviceToDuplicate.toServe ?? '',
        type: serviceToDuplicate.type,
        data: {
          type: serviceToDuplicate.type ?? undefined,
          toServe: serviceToDuplicate?.toServe ?? '',
          address1: serviceToDuplicate?.address1 ?? '',
          address2: serviceToDuplicate?.address2,
          city: serviceToDuplicate?.city ?? '',
          phone: serviceToDuplicate?.phone ?? '',
          addressType: serviceToDuplicate?.addressType ?? '',
          state: serviceToDuplicate?.state ?? '',
          zipCode: serviceToDuplicate?.zipCode ?? '',
          documents: serviceToDuplicate?.documents,
          serverInstructions: serviceToDuplicate?.serverInstructions ?? '',
          includeHomeInspection: serviceToDuplicate?.includeHomeInspection,
          includeHomeInspectionManualOverride: serviceToDuplicate?.includeHomeInspectionManualOverride,
        },
      };
      if (services && duplicatedService) setServices([...services, duplicatedService]);
      reset({
        ...getValues(),

        workOrders: [...(getValues('workOrders') as WorkOrderSchema[]), duplicatedService.data],
      });
      setActiveServiceId(dupServiceId);
    }
  };

  const deleteService = (id: string) => {
    const newServices = services.filter((service) => service.id !== id);
    const deleteServiceIndex = services.findIndex((service) => service.id === id);
    const updatedData = getValues().workOrders as WorkOrderSchema[];
    setDeleteIndex(deleteServiceIndex);

    const updatedWorkOrders = updatedData ? updatedData.filter((_, index: number) => index !== deleteServiceIndex) : [];
    setValue('workOrders', updatedWorkOrders, {
      shouldDirty: true,
    });
    setServices(newServices);
    if (newServices.length > 0) {
      const nextServiceIndex = deleteServiceIndex < newServices.length ? deleteServiceIndex : newServices.length - 1;
      setActiveServiceId(newServices[nextServiceIndex].id);
    } else {
      setActiveServiceId('');
    }
  };

  const duplicateWithAddress = (id: string) => {
    const serviceToDuplicate = services.find((service) => service.id === id);

    const dupServiceId = genUniqueId();
    if (serviceToDuplicate) {
      const serviceIndex = services.findIndex((p) => p.id === id);
      const duplicatedService = {
        id: dupServiceId,
        name: 'New Service',
        type: undefined,
        data: {
          address1: serviceToDuplicate?.data?.address1 ?? '',
          address2: serviceToDuplicate?.data?.address2,
          addressType: serviceToDuplicate?.data?.addressType ?? '',
          city: serviceToDuplicate?.data?.city ?? '',
          state: serviceToDuplicate?.data?.state ?? '',
          zipCode: serviceToDuplicate?.data?.zipCode ?? '',
          serveTo: serviceToDuplicate?.data?.serveTo,
          serverId: serviceToDuplicate?.data?.serverId,
        },
      };
      setServices([...services, duplicatedService]);
      reset({
        ...getValues(),
        workOrders: [
          ...(getValues('workOrders') as WorkOrderSchema[]),
          {
            ...duplicatedService.data,
            address1: getValues(`workOrders[${serviceIndex}].address1`) as string,
            address2: getValues(`workOrders[${serviceIndex}].address2`) as string,
            city: getValues(`workOrders[${serviceIndex}].city`) as string,
            state: getValues(`workOrders[${serviceIndex}].state`) as string,
            zipCode: getValues(`workOrders[${serviceIndex}].zipCode`) as string,
            serveTo: getValues(`workOrders[${serviceIndex}].serveTo`) as string,
            serverId: getValues(`workOrders[${serviceIndex}].serverId`) as string,
          },
        ],
      });
      setActiveServiceId(dupServiceId);
    }
  };
  const duplicateWithDocuments = (id: string) => {
    const serviceToDuplicate = services.find((service) => service.id === id);
    const dupServiceId = genUniqueId();
    if (serviceToDuplicate) {
      const serviceIndex = services.findIndex((p) => p.id === id);
      const duplicatedService = {
        id: dupServiceId,
        name: 'New Service',
        type: serviceToDuplicate.type,
        data: {
          documents: serviceToDuplicate?.data?.documents,
        },
      };
      setServices([...services, duplicatedService]);
      reset({
        ...getValues(),
        workOrders: [
          ...(getValues('workOrders') as WorkOrderSchema[]),
          {
            ...duplicatedService.data,
            affidavitTypeId: getValues(`workOrders[${serviceIndex}].affidavitTypeId`) as string,
            documents: getValues(`workOrders[${serviceIndex}].documents`) as string[],
          },
        ],
      });
      setActiveServiceId(dupServiceId);
    }
  };

  const duplicateWithInstructions = (id: string) => {
    const serviceToDuplicate = services.find((service) => service.id === id);
    const dupServiceId = genUniqueId();
    if (serviceToDuplicate) {
      const serviceIndex = services.findIndex((p) => p.id === id);
      const duplicatedService = {
        id: dupServiceId,
        name: 'New Service',
        type: serviceToDuplicate.type,
        data: {
          serverInstructions: serviceToDuplicate?.data?.serverInstructions,
        },
      };
      setServices([...services, duplicatedService]);
      reset({
        ...getValues(),
        workOrders: [
          ...(getValues('workOrders') as WorkOrderSchema[]),
          {
            ...duplicatedService.data,
            serverInstructions: getValues(`workOrders[${serviceIndex}].serverInstructions`) as string,
          },
        ],
      });
      setActiveServiceId(dupServiceId);
    }
  };

  useEffect(() => {
    const serverDetails = getValues('workOrders') as WorkOrderSchema[] | undefined;
    if (serverDetails && serverDetails.length > 0) {
      const newServices = serverDetails.map((service, index) => ({
        id: service.workOrderId ?? services[index]?.id ?? genUniqueId(),
        name: service?.toServe ?? `New Service`,
        type: service.type,
        data: service,
      }));
      setServices(newServices);
      
      const targetService = initialActiveWorkOrderId
        ? newServices.find((s) => s.id === initialActiveWorkOrderId || s.data?.workOrderId === initialActiveWorkOrderId)
        : undefined;

      if (targetService) {
        setActiveServiceId(targetService.id);
      } else {
        setActiveServiceId(newServices[0]?.id);
      }
    }
  }, []);

  const [, setHasPrivateInfo] = useState(false);

  const privateInfoCheckbox = watch(
    `workOrders[${services.findIndex((p) => p.id === activeServiceId)}].hasPrivateInfo`
  ) as boolean;

  useEffect(() => {
    if (privateInfoCheckbox === false) {
      setOpenPrivateInfo(false);
    }
  }, [privateInfoCheckbox]);

  const type = watch(`workOrders[${services.findIndex((p) => p.id === activeServiceId)}].type`) as string;
  const casetype = watch('case.typeId') as string;

  const serveTo = watch(
    `workOrders[${services.findIndex((p) => p.id === activeServiceId)}].serveTo`
  ) as WorkOrderServeTo;
  const currentWoTeamId = watch(`workOrders[${services.findIndex((p) => p.id === activeServiceId)}].teamId`) as string;

  const { data: affidavitType, isFetching: isAffidavitTypeFetching } = useQuery({
    queryFn: () =>
      affidavitTypeGetAll({
        caseTypeId: casetype,
      }),
    queryKey: ['affidavitType', casetype],
  });

  const { data: caseDocs, isFetching: isCaseDocsFetching } = useQuery({
    queryFn: () => caseDocumentTypeGetAll(),
    queryKey: ['caseDocs', docRefreshkey],
  });

  const affidavitOptions = useMemo(() => {
    if (affidavitType?.data.affidavitTypes)
      return affidavitType.data.affidavitTypes.map((item) => ({
        label: item.name,
        value: item.affidavitTypeId,
      }));
    return [];
  }, [affidavitType]);

  const caseDocumentOptions = useMemo(() => {
    if (caseDocs?.data.caseDocumentTypes)
      return caseDocs.data.caseDocumentTypes.map((item) => ({
        label: item.docType.name,
        value: item.caseDocTypeId,
        caseTypeId: item.caseTypeId,
      }));
    return [];
  }, [caseDocs]);
  useEffect(() => {
    setIsSupervisorOrAdmin(isAdminOrSupervisor(userStore.user));
  }, [userStore.user]);

  return (
    <Loader isLoading={isAffidavitTypeFetching} className="min-h-[60vh]">
      <Fragment key={activeServiceId}>
        {(() => {
          const currentWorkOrderIndex = services.findIndex((service) => service.id === activeServiceId);
          if (currentWorkOrderIndex === -1) {
            let newIndex = services.length - 1;
            if (deleteIndex)
              if (deleteIndex === 0) newIndex = 1;
              else newIndex = deleteIndex - 1;
            setActiveServiceId(services[newIndex].id);
            return null;
          }
          return (
            <div className="flex flex-col gap-8 mt-10">
              <div className="flex flex-col gap-6">
                <div className=" bg-[#F5F5F5] h-12 flex overflow-x-scroll overflow-y-clip scrollbar-hide ">
                  {services.map((service) => {
                    return (
                      <div
                        key={service.id}
                        onClick={() => handleServiceSwitch(service.id)}
                        className={`p-2 flex min-w-fit flex-row cursor-pointer align-middle items-center  justify-between gap-2 rounded-none  hover:none text-primary ${activeServiceId === service.id ? 'bg-[#EDF5FF]  text-primary' : 'bg-[#E2E2E2] text-primary cursor-pointer '}`}
                      >
                        {service.type == 'INDIVIDUAL' ? (
                          <User color="#0A2240" />
                        ) : service.type === 'BUSINESS' ? (
                          <Building color="#0A2240" />
                        ) : null}
                        {service.name.length > 20 ? `${service.name.slice(0, 20)}...` : service.name}

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <div className="cursor-pointer">
                              <ChevronDown color="#676767" />
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-[275px]">
                            <DropdownMenuItem onClick={() => duplicateWithDocuments(service.id)}>
                              <div className="flex flex-row justify-start items-center gap-2 py-1.5 px-2  w-full font-[500] text-3.5">
                                <Copy color="#334155" className="w-4 h-4" /> <span>Duplicate Documents</span>
                              </div>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicateWithAddress(service.id)}>
                              <div className="flex flex-row justify-start items-center gap-2 py-1.5 px-2  w-full font-[500] text-3.5">
                                <Copy color="#334155" className="w-4 h-4" /> <span>Duplicate Address</span>
                              </div>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicateWithInstructions(service.id)}>
                              <div className="flex flex-row justify-start items-center gap-2 py-1.5 px-2  w-full font-[500] text-3.5">
                                <Copy color="#334155" className="w-4 h-4" /> <span>Duplicate Instructions</span>
                              </div>
                            </DropdownMenuItem>
                            {services.length > 1 && (
                              <DropdownMenuItem onClick={() => deleteService(service.id)}>
                                <div className="flex flex-row justify-start items-center gap-2 py-1.5 px-2  w-full font-[500] text-3.5">
                                  <Trash2 color="#D72222" className="w-4 h-4" />
                                  <span className="text-[#D72222]">Delete</span>
                                </div>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })}
                  <Button
                    onClick={() => {
                      duplicateService(activeServiceId);
                    }}
                    className="p-2 bg-primary h-12 w-12 xt-white flex jusfy-center align-middle rounded-none"
                  >
                    <Plus />
                  </Button>
                </div>
                <div className={`flex gap-6 w-full `}>
                  <div className={`w-[20vw]`}>
                    <FormFieldSelect
                      name={`workOrders[${currentWorkOrderIndex}].type`}
                      options={SERVE_TYPE}
                      placeholder="Select Type"
                      required
                      label="Type"
                      onChange={(value) => {
                        const newServices = [...services];
                        newServices.find((service) => service.id === activeServiceId)!.type = value;
                        setServices(newServices);
                        unregister([
                          `workOrders[${currentWorkOrderIndex}].privateInfo`,
                          `workOrders[${currentWorkOrderIndex}].toServe`,
                        ]);
                        setValue(`workOrders[${currentWorkOrderIndex}].hasPrivateInfo`, false);
                      }}
                    />
                  </div>
                  {type && (
                    <div className="w-[70vw]">
                      <FormFieldComponent
                        control={control}
                        name={`workOrders[${currentWorkOrderIndex}].toServe`}
                        label={type === 'INDIVIDUAL' ? 'Person to Serve' : 'Business Name'}
                        placeholder={type === 'INDIVIDUAL' ? 'Enter Person to Serve' : 'Enter Business Name'}
                        required
                        capitalizeFirstLetter
                        onChange={(e) => {
                          const newServices = [...services];
                          newServices.find((service) => service.id === activeServiceId)!.name = e.target.value;
                          setServices(newServices);
                        }}
                      />
                    </div>
                  )}
                </div>
                <FormFieldRadioGroup
                  label="Address Type:"
                  name={`workOrders[${currentWorkOrderIndex}].addressType`}
                  options={[
                    { value: 'HOME', label: 'Home' },
                    { value: 'BUSINESS', label: 'Business' },
                  ]}
                  direction="horizontal"
                  required
                />
                <FormFieldComponent
                  control={control}
                  name={`workOrders[${currentWorkOrderIndex}].address1`}
                  label="Address Line 1"
                  placeholder="Enter Street Address"
                  required
                  {...ADDRESS_LINE_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name={`workOrders[${currentWorkOrderIndex}].address2`}
                  label="Address Line 2"
                  placeholder="Enter Apt, suite etc. (Optional)"
                  {...ADDRESS_LINE_VALIDATION}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormFieldComponent
                    name={`workOrders[${currentWorkOrderIndex}].city`}
                    label="City"
                    placeholder="City"
                    control={control}
                    required
                  />
                  <FormFieldComponent
                    control={control}
                    name={`workOrders[${currentWorkOrderIndex}].state`}
                    label="State"
                    placeholder="State"
                    required
                    {...STATE_VALIDATION}
                  />
                  <FormFieldComponent
                    control={control}
                    name={`workOrders[${currentWorkOrderIndex}].zipCode`}
                    label="Zip Code"
                    placeholder="Zip Code"
                    required
                    {...ZIP_CODE_VALIDATION}
                    onChange={() => {
                      setValue(`workOrders[${currentWorkOrderIndex}].vendorDetails.vendorId`, null);
                      setValue(`workOrders[${currentWorkOrderIndex}].vendorDetails.vendorContactId`, null);
                    }}
                  />
                </div>
              </div>

              <div className="flex w-[33%] pr-4 flex-col gap-6">
                <FormFieldPhone
                  name={`workOrders[${currentWorkOrderIndex}].phone`}
                  label="Phone Number"
                  defaultCountry="us"
                  itemClassName="w-full"
                />
                {canShowIncludeHomeInspection && (
                  <FormFieldCheckbox
                    control={control}
                    name={`workOrders[${currentWorkOrderIndex}].includeHomeInspection`}
                    label="Include Home Inspection"
                    disabled={includeHomeInspectionDisabled}
                    onCheckChange={() => {
                      setValue(`workOrders[${currentWorkOrderIndex}].includeHomeInspectionManualOverride`, true, {
                        shouldDirty: true,
                      });
                    }}
                  />
                )}
              </div>
              {getValues(`workOrders[${currentWorkOrderIndex}].type`) === 'INDIVIDUAL' && (
                <div className="flex flex-col justify-between md:flex-row  items-start ">
                  <FormFieldCheckbox
                    control={control}
                    name={`workOrders[${currentWorkOrderIndex}].hasPrivateInfo`}
                    label="Private Information"
                    onCheckChange={(value) => {
                      setHasPrivateInfo(value);
                      if (!value) unregister(`workOrders[${currentWorkOrderIndex}].privateInfo`);
                      else {
                        setValue(`workOrders[${currentWorkOrderIndex}].privateInfo`, {
                          ssn: null,
                          dateOfBirth: null,
                          dlNumber: null,
                          others: null,
                        });
                      }
                    }}
                  />

                  {getValues(`workOrders[${currentWorkOrderIndex}].hasPrivateInfo`) &&
                    getValues(`workOrders[${currentWorkOrderIndex}].type`) === 'INDIVIDUAL' && (
                      <div className=" flex flex-col gap-2">
                        <Collapsible
                          className="flex flex-col bg-[#F5F5F5] w-[60vw] px-3 rounded-md cursor-pointer"
                          onBlurCapture={async (e) => {
                            if (!e.relatedTarget?.classList.contains('calendar-nav-button'))
                              if (!e.currentTarget.contains(e.relatedTarget)) {
                                await trigger(`workOrders[${currentWorkOrderIndex}].privateInfo`);
                              }
                          }}
                        >
                          <CollapsibleTrigger
                            className="flex flex-row justify-between p-3 "
                            onClick={handlePrivateInfo}
                          >
                            <div className="flex flex-row gap-1">
                              <span className="text-base font-[600]"> Private Information</span>
                              <span className="text-error">*</span>
                            </div>
                            {openPrivateInfo ? <ChevronUp /> : <ChevronDown />}
                          </CollapsibleTrigger>
                          {openPrivateInfo && (
                            <div className="flex flex-col bg-[#F5F5F5] gap-4 px-3 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormFieldMaskedInput
                                  control={control}
                                  name={`workOrders[${currentWorkOrderIndex}].privateInfo.ssn`}
                                  label="Social Security Number"
                                  props={{
                                    maxLength: 11,
                                  }}
                                  placeholder="Enter Social Security Number"
                                  isPasswordRequired
                                  type="number"
                                  enableFormatting
                                />

                                <FormFieldCalendar
                                  control={control}
                                  name={`workOrders[${currentWorkOrderIndex}].privateInfo.dateOfBirth`}
                                  label="Date of Birth"
                                  disabledAt={(date: Date) => date > new Date()}
                                  placeholder="Select Date of Birth"
                                />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormFieldMaskedInput
                                  control={control}
                                  name={`workOrders[${currentWorkOrderIndex}].privateInfo.dlNumber`}
                                  label="Driver License"
                                  isPasswordRequired
                                  placeholder="Enter Driver License"
                                  formatChar="*"
                                />
                                <FormFieldComponent
                                  control={control}
                                  name={`workOrders[${currentWorkOrderIndex}].privateInfo.others`}
                                  label="Others"
                                  placeholder="Enter Other Details"
                                />
                              </div>
                            </div>
                          )}
                        </Collapsible>
                        {getFieldState(`workOrders[${currentWorkOrderIndex}].privateInfo`)?.error?.message && (
                          <p className="text-error text-xs">
                            {getFieldState(`workOrders[${currentWorkOrderIndex}].privateInfo`)?.error?.message}
                          </p>
                        )}
                      </div>
                    )}
                </div>
              )}

              {caseTypesWithWitnessFees.includes(casetype) && (
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-12 md:col-span-2">
                    <FormFieldCheckbox
                      control={control}
                      name={`workOrders[${currentWorkOrderIndex}].hasWitnessFees`}
                      label="Witness Fees"
                      onCheckChange={(value) => {
                        if (!value) unregister(`workOrders[${currentWorkOrderIndex}].witnessFee`);
                      }}
                    />
                  </div>

                  <div className="col-span-12 md:col-span-10">
                    {watch(`workOrders[${currentWorkOrderIndex}].hasWitnessFees`) && (
                      <WorkOrderPaymentForm
                        header="Witness Fee Check"
                        name={`workOrders[${currentWorkOrderIndex}].witnessFee`}
                        disabled={[WorkOrderPaymentStatus.VOID, WorkOrderPaymentStatus.CASHED].includes(
                          getValues(`workOrders[${currentWorkOrderIndex}].witnessFee.status`) as WorkOrderPaymentStatus
                        )}
                        paymentType={PaymentType.WITNESS_FEE}
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col">
                <FormFieldSelect
                  name={`workOrders[${currentWorkOrderIndex}].affidavitTypeId`}
                  options={affidavitOptions}
                  placeholder="Select Affidavit Type"
                  label="Affidavit Type"
                  required
                />
              </div>

              <div className="flex flex-col">
                <FormFieldRadioGroup
                  name={`workOrders[${currentWorkOrderIndex}].serveTo`}
                  options={[
                    {
                      label: 'Server',
                      value: WorkOrderServeTo.SERVER,
                    },
                    {
                      label: 'Vendor',
                      value: WorkOrderServeTo.VENDOR,
                    },
                  ]}
                  direction="horizontal"
                  label="Assign To:"
                />
              </div>

              {serveTo === WorkOrderServeTo.VENDOR ? (
                <div className="flex flex-col gap-6">
                  <div className="font-[600] text-lg">Vendor Details</div>
                  <WorkOrderVendorForm currentWorkOrderIndex={currentWorkOrderIndex} />
                </div>
              ) : serveTo === WorkOrderServeTo.SERVER ? (
                <div className="flex flex-col gap-6">
                  <div className="font-[600] text-lg">Server Details</div>
                  <FormFieldSearch
                    name={`workOrders[${currentWorkOrderIndex}].serverId`}
                    label="Server Name"
                    placeholder="Assign Server"
                    availability
                    props={SearchIconsProps}
                    api={serverSearch}
                  />
                </div>
              ) : null}

              <FormFieldTextArea
                rows={4}
                name={`workOrders[${currentWorkOrderIndex}].serverInstructions`}
                label="Special Instructions"
                placeholder=""
              />

              <div className="flex flex-col gap-3">
                <Collapsible className="w-full bg-[#F5F5F5] flex flex-col !rounded-md font-semibold justify-between">
                  <CollapsibleTrigger
                    className="flex justify-between align-middle rounded-md items-center w-full bg-[#F5F5F5] pr-3"
                    onClick={async () => {
                      setOpen(!open);
                      await trigger(`workOrders[${currentWorkOrderIndex}].documents`);
                    }}
                    onBlur={async () => await trigger(`workOrders[${currentWorkOrderIndex}].documents`)}
                  >
                    <div className="flex flex-row p-3">
                      <span>Description of Documents</span>&nbsp;<span className="text-error">*</span>
                    </div>
                    {!open ? <ChevronDown color="#969696" /> : <ChevronUp color="#969696" />}
                  </CollapsibleTrigger>

                  <CollapsibleContent className={`gap-4 flex flex-col !p-0`}>
                    <div className="flex bg-[#FFFFFF] flex-col gap-2 mx-3 p-6 pb-3 my-2">
                      <Loader isLoading={isCaseDocsFetching}>
                        {caseDocumentOptions
                          .filter((document) => document.caseTypeId === getValues(`case.typeId`))
                          .map((document) => (
                            <FormFieldCheckbox
                              key={document.value}
                              control={control}
                              name={`workOrders[${currentWorkOrderIndex}].documents.${document.value}`}
                              label={document.label}
                              onCheckChange={async () => {
                                await trigger(`workOrders[${currentWorkOrderIndex}].documents`);
                              }}
                            />
                          ))}
                      </Loader>
                    </div>
                    {!addDocumentField && (
                      <div
                        className="flex justify-end items-center cursor-pointer gap-2 p-3"
                        onClick={() => setAddDocumentField(!addDocumentField)}
                      >
                        <Plus className="text-[#0A2240] h-4 w-4 " />
                        <span color="1551B2" className="text-[14px] font-medium align-middle text-focuscolor">
                          Add Document
                        </span>
                      </div>
                    )}
                    {addDocumentField && (
                      <div className="flex flex-col gap-4 p-3">
                        <div className="flex flex-col gap-2">
                          <FormFieldComponent
                            control={control}
                            name={`otherDocName`}
                            label="Description of the Document"
                            placeholder="Enter Description of the Document"
                            onChange={(e) => setNewDocument(e.target.value)}
                            maxLength={30}
                            required
                            replaceRegex={/[^a-zA-Z ]/g}
                          />
                          {!newDocument && touchedFields.otherDocName && (
                            <p className="text-error text-xs font-normal ml-1">
                              Description of the document is required
                            </p>
                          )}
                        </div>
                        <div className="gap-4 flex flex-row">
                          <Button
                            onClick={() => {
                              unregister('otherDocName');
                              setAddDocumentField(false);
                            }}
                            variant="outline"
                            className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[36px] hover:bg-[#EDF5FF]"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => {
                              docCreateHandler(getValues(`case.typeId`) as string, newDocument);
                            }}
                            className="text-[#fff] py-2 px-4 rounded-md bg-primary  w-[125px] h-[36px] border-primaryflex justify-center  hover:bg-[#12375C] hover:text-white disabled:bg-primary"
                            isLoading={caseDocTypeCreateStatus === 'pending'}
                            disabled={caseDocTypeCreateStatus === 'pending' || !newDocument}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
                {caseDocTypeCreateError?.error.details ? (
                  <p className="text-error text-xs">
                    {typeof caseDocTypeCreateError?.error.details === 'string'
                      ? caseDocTypeCreateError?.error.details
                      : 'An error occurred. Please try again later.'}
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-6">
                <FormFieldSearch
                  name={`workOrders[${currentWorkOrderIndex}].teamId`}
                  label="Assign Team"
                  placeholder="Search Team"
                  props={SearchIconsProps}
                  api={teamSearch}
                  onBlur={(value) => {
                    if (!value) setValue(`workOrders[${currentWorkOrderIndex}].teamId`, null, { shouldDirty: true });
                  }}
                  onChange={() => {
                    setValue(`workOrders[${currentWorkOrderIndex}].userId`, null, { shouldDirty: true });
                  }}
                />
                {currentWoTeamId && (
                  <FormFieldSearch
                    name={`workOrders[${currentWorkOrderIndex}].userId`}
                    label="Assign User"
                    placeholder="Search User"
                    props={SearchIconsProps}
                    searchFieldName="search"
                    disabled={!isSupervisorOrAdmin}
                    api={(params) =>
                      getUsersByTeam(
                        {
                          teamId: currentWoTeamId,
                          userName: params.search ?? undefined,
                          userStatus: 'ACTIVE',
                        },
                        isSupervisorOrAdmin
                      )
                    }
                    onBlur={(value) => {
                      if (!value) setValue(`workOrders[${currentWorkOrderIndex}].userId`, null, { shouldDirty: true });
                    }}
                  />
                )}
              </div>

              {hasChecksFeature && (
                <div className="flex flex-col gap-2">
                  <WorkOrderPayments
                    paymentType={PaymentType.OPERATING_CHECK}
                    currentWorkOrderIndex={currentWorkOrderIndex}
                  />
                </div>
              )}
            </div>
          );
        })()}
      </Fragment>
    </Loader>
  );
};

export default PeWoDetails;
